// @ts-strict-ignore
import { Log } from '@shared/debug/log';
import JSZip from 'jszip';
import { firstValueFrom } from 'rxjs';
import { TextureUtils } from '@3d-scene/model3d/util/texture-utils';
import { MultipleFilesChosenException } from '@project/components/import-export/processor/multiple-files-chosen-exception';
import { NoFileChosenException } from '@project/components/import-export/processor/no-file-chosen-exception';
import { AlertHelper } from '../alert/alert.helper';
import { ArrayUtils } from './array-utils';
import { DeviceUtils } from './device-utils';
import { StringUtils } from './string-utils';

export type FileExtension =
  | '.xml'
  | '.csv'
  | '.pdf'
  | '.html'
  | '.wsix'
  | '.wscx'
  | '.jpg'
  | '.jpeg'
  | '.png'
  | '.wsp'
  | '.obz'
  | '.zip'
  | '.glb'
  | '.txt'
  | '.json'

export enum FileReadingType {
  TEXT,
  DATA_URL,
}

export interface LoadedImage {
  file: File;
  image: HTMLImageElement;
}

export class FileUtils {

  public static saveTextFile(fileName: string, fileContent: any, fileExtension: FileExtension, isDecodingRequired: boolean = false): void {
    const isFileSaverSupported = !!new Blob();
    if (!isFileSaverSupported) {
      throw Error('Error: FileUtils: FileSaver is not supported!');
    }
    if (StringUtils.isEmpty(fileName) || StringUtils.isEmpty(fileContent) || StringUtils.isEmpty(fileExtension)) {
      return;
    }
    let content: string = fileContent;
    if (isDecodingRequired) {
      content = atob(fileContent);
    }
    const contentType: string = this.getContentTypeByExtension(fileExtension);
    let data: any;
    const link: HTMLAnchorElement = document.createElement('a');
    if (DeviceUtils.isMobile()) {
      data = `data:${contentType};charset=utf-8,` + content;
    } else {
      const blob: Blob = new Blob([content], { type: `${contentType};charset=utf-8;` });
      data = window.URL.createObjectURL(blob);
    }
    link.href = data;
    link.download = `${fileName}${fileExtension}`;
    link.target = '_blank';
    link.onclick = (event: any) => document.body.removeChild(event.target);
    document.body.appendChild(link);
    link.click();
  }

  private static getContentTypeByExtension(fileExtension: FileExtension): string {
    switch (fileExtension) {
      case '.xml':
        return 'text/xml;charset:UTF-8';
      case '.csv':
        return 'text/csv';
      case '.pdf':
        return 'application/pdf';
      case '.html':
        return 'text/html';
      case '.wscx':
        return 'text/wsix';
      case '.wsix':
        return 'text/wscx';
      case '.txt':
        return 'text/txt';
      default:
        return 'text/plain';
    }
  }

  public static async saveFilesAsZip(
    files: { fileName: string; fileContent: any; fileExtension: FileExtension; isDecodingRequired?: boolean }[],
    zipName: string = 'download'
  ): Promise<void> {
    const zip = new JSZip();

    for (const file of files) {
      if (!file.fileName || !file.fileContent || !file.fileExtension) {
        Log.error("could not add file to zip - file is invalid:", file)
        continue;
      }

      const blob = this.contentToBlob(file.fileContent, file.fileExtension, file.isDecodingRequired ?? false);
      zip.file(`${file.fileName}${file.fileExtension}`, blob);
    }

    const zipBlob = await zip.generateAsync({ type: 'blob' });

    const link: HTMLAnchorElement = document.createElement('a');
    link.href = URL.createObjectURL(zipBlob);
    link.download = zipName + ".zip";
    link.target = '_blank';
    link.onclick = (event: any) => document.body.removeChild(event.target);
    document.body.appendChild(link);
    link.click();
  }

  public static saveBinaryFile(fileName: string, fileContent: any, fileExtension: FileExtension, isDecodingRequired: boolean = false): void {
    const isFileSaverSupported = !!new Blob();
    if (!isFileSaverSupported) {
      throw Error('Error: FileUtils: FileSaver is not supported!');
    }
    if (StringUtils.isEmpty(fileName) || !fileContent || StringUtils.isEmpty(fileExtension)) {
      return;
    }
    const blob = this.contentToBlob(fileContent, fileExtension, isDecodingRequired);
    const link: HTMLAnchorElement = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = `${fileName}${fileExtension}`;
    link.target = '_blank';
    link.onclick = (event: any) => document.body.removeChild(event.target);
    document.body.appendChild(link);
    link.click();
  }

  private static contentToBlob(content: any, fileExtension: FileExtension, isDecodingRequired: boolean = false) {
    if (typeof content === 'string') {
      content = this.byteStringToArrayBuffer(content, isDecodingRequired);
    }
    return new Blob([content], { type: this.getContentTypeByExtension(fileExtension) });
  }

  public static async loadImageFile(alertHelper: AlertHelper, ...fileExtensions: FileExtension[]): Promise<LoadedImage> {
    const imageFile: File = await FileUtils.loadFile(...fileExtensions);
    const extension: string = this.getFileExtension(imageFile);
    const fileData: string = await this.readFile(imageFile, FileReadingType.DATA_URL);
    const image: HTMLImageElement = await firstValueFrom(TextureUtils.createImage(fileData, extension, '', false))
    const validatedImage: HTMLImageElement = TextureUtils.validateCustomImage(image, alertHelper);
    return {file: imageFile, image: validatedImage};
  }

  public static getFileExtension(file: File): string {
    if (!file || !(file instanceof File)) {
      return null;
    }
    return file.name.split('.').pop().toLocaleLowerCase();
  }

  public static readFile(file: File, readingType: FileReadingType = FileReadingType.TEXT): Promise<string> {
    if (!file) {
      return null;
    }
    return new Promise<string>((resolve, reject) => {
      const fileReader: FileReader = new FileReader();
      fileReader.onload = (evt: Event) => {
        const reader: FileReader = evt.target as FileReader;
        const fileContent: string = reader.result as string;
        resolve(fileContent);
      };
      fileReader.onerror = (evt: Event) => reject(evt);
      switch (readingType) {
        case FileReadingType.TEXT:
          return fileReader.readAsText(file);
        case FileReadingType.DATA_URL:
          return fileReader.readAsDataURL(file);
        default:
          return fileReader.readAsText(file);
      }
    });
  }

  public static async loadTextFile(...fileExtensions: FileExtension[]): Promise<string> {
    const file: File = await FileUtils.loadFile(...fileExtensions);
    return this.readFile(file);
  }

  public static async loadFile(...fileExtensions: FileExtension[]): Promise<File> {
    const fileInput: HTMLInputElement = document.createElement('input');
      let onChangeEventReceived: boolean = false;
      let windowFocusEventHandler = null;
      let fileInputChangeEventHandler = null;
    fileInput.type = 'file';
    if (ArrayUtils.isNotEmpty(fileExtensions)) {
      fileInput.accept = fileExtensions.join();
    }
    return new Promise<File>((resolve, reject) => {
      fileInputChangeEventHandler = () => {
        if (onChangeEventReceived) {
          return; // do not process duplicated events from focus and file input events
        }
        onChangeEventReceived = true;
        if (windowFocusEventHandler != null) {
          window.removeEventListener('focus', windowFocusEventHandler);
          windowFocusEventHandler = null;
        }
        const files: FileList = fileInput.files;
        if (!files || files.length === 0) {
          reject(new NoFileChosenException('File dialog was cancelled by the user'));
        } else if (files.length > 1) {
          reject(new MultipleFilesChosenException('More then one file was chosen by the user'));
        } else {
          const file: File = files.item(0);
          resolve(file);
        }
      };
      windowFocusEventHandler = () => {
        if (windowFocusEventHandler != null) {
          window.removeEventListener('focus', windowFocusEventHandler);
          windowFocusEventHandler = null;
        }
        // We listen for window getting focus to know if file dialog has been closed.
        // There is no specific possibility to listen for a close/cancel event on file input dialog.
        // We need a delay because there is no dedicated order of change and focus events and thus
        // when receiving the focus event the selected files might not have been set even though window has already the focus.
        setTimeout(fileInputChangeEventHandler, 1000);
      };
      fileInput.addEventListener('change', fileInputChangeEventHandler);
      window.addEventListener('focus', windowFocusEventHandler);
      fileInput.click();
    });
  }

  public static handleFileLoadingError(error: any, alertHelper: AlertHelper): void {
    if (error instanceof NoFileChosenException) {
      return;
    }
    Log.error('Could not load file: ', error);
    if (error instanceof MultipleFilesChosenException) {
      alertHelper.showError('importProject.title.import_failed', 'importProject.message.import_failed_due_to_multiple_files_selected');
      return;
    }
    alertHelper.showError('importProject.title.import_failed', 'importProject.message.import_failed');
  }

  public static byteStringToArrayBuffer(fileContent: string, isDecodingRequired: boolean = false) {
    let binaryString = fileContent;
    if (isDecodingRequired) {
      binaryString = atob(fileContent);
    }
    const binaryLen = binaryString.length;
    const bytes = new Uint8Array(binaryLen);
    for (let i = 0; i < binaryLen; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }


  public static async unzipFileSecured(file: File): Promise<JSZip.JSZipObject[]> {
    const MAX_FILES = 1000;
    const MAX_SIZE = 100000000; // 100 MB

    let zip: any;
    try {
      zip = await JSZip.loadAsync(file);
    } catch (error) {
      Log.error('Could not load zip file: ', error);
      throw error;
    }

    const fileEntries: JSZip.JSZipObject[] = [];
    zip.forEach((_relativePath: string, zipEntry: JSZip.JSZipObject) => {
      if (!zipEntry.dir) {
        fileEntries.push(zipEntry);
      }
    });

    if (fileEntries.length > MAX_FILES) {
      throw new Error('Reached max. number of files');
    }

    const files : JSZip.JSZipObject[] = [];
    let totalSize = 0;

    for (const zipEntry of fileEntries) {
      const content = await zip.file(zipEntry.name).async('uint8array');
      totalSize += content.length;
      if (totalSize > MAX_SIZE) {
        throw new Error('Reached max. size');
      }
      files.push(zipEntry);
    }

    return files;
  }

}
